import dayjs, { Dayjs } from "dayjs";
import { parseLocalDateTime } from "src/utils/parseLocalDateTime";
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  CraneShift,
  generatedApi,
  useGetStrategyDashboardShiftsQuery,
} from "src/store/api/generatedApi";
import { useTenant } from "hooks/settings";
import { shiftsReducer } from "./reducer";
import {
  shiftsActions,
  initialShiftsState,
  SHIFTS_PER_FETCH,
  UseShiftsOptions,
  UseShiftsResult,
} from "./types";
import { useShiftBuffer } from "./useShiftBuffer";
import { loadShiftsFromCache, saveShiftsToCache } from "./useShiftsCache";

export const useShifts = (options: UseShiftsOptions = {}): UseShiftsResult => {
  const { initialShiftStartTime, onShiftChange, bufferSize = 0 } = options;
  const tenantName = useTenant();
  const [fetchShifts] =
    generatedApi.endpoints.getStrategyDashboardShifts.useLazyQuery();
  const [state, dispatch] = useReducer(shiftsReducer, initialShiftsState);

  const referenceTimestamp = useMemo(() => {
    const now = dayjs();
    if (initialShiftStartTime) {
      const parsed = dayjs(initialShiftStartTime);
      if (parsed.isValid() && !parsed.isAfter(now)) {
        return parsed.toISOString();
      }
    }
    return now.toISOString();
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- computed once on mount; subsequent changes to initialShiftStartTime are handled by the reducer, not by re-querying
  }, []);

  // Selection occurs after matching shifts enter the reducer.
  useEffect(() => {
    dispatch(shiftsActions.setTargetTime(referenceTimestamp));
  }, [referenceTimestamp]);

  const { data, isFetching, isLoading, isError } =
    useGetStrategyDashboardShiftsQuery({
      tenantName,
      referenceTimestamp,
      before: SHIFTS_PER_FETCH,
      after: SHIFTS_PER_FETCH,
    });
  const [overrideLoading, setOverrideLoading] = useState(false);

  const cacheInitializedRef = useRef(false);
  useEffect(() => {
    if (cacheInitializedRef.current) return;
    cacheInitializedRef.current = true;
    const cached = loadShiftsFromCache(tenantName);
    if (cached && cached.length > 0) {
      dispatch(shiftsActions.addShifts(cached, true));
    }
  }, [tenantName]);

  const apiInitializedRef = useRef(false);
  useEffect(() => {
    if (!data?.shifts || data.shifts.length === 0) return;
    if (apiInitializedRef.current) return;
    apiInitializedRef.current = true;

    dispatch(shiftsActions.addShifts(data.shifts, false));
  }, [data?.shifts]);

  useEffect(() => {
    if (state.shifts.length > 0) {
      saveShiftsToCache(tenantName, state.shifts);
    }
  }, [tenantName, state.shifts]);

  useShiftBuffer({
    shifts: state.shifts,
    selectedIndex: state.selectedIndex,
    bufferSize,
    dispatch,
  });

  const selectedShift = useMemo(
    () => state.shifts[state.selectedIndex] ?? null,
    [state.shifts, state.selectedIndex]
  );

  useEffect(() => {
    if (selectedShift?.shift_start_time && onShiftChange) {
      onShiftChange(selectedShift.shift_start_time);
    }
  }, [selectedShift?.shift_start_time, onShiftChange]);

  const canGoBack = state.selectedIndex > 0;
  const hasNextShift = state.selectedIndex < state.shifts.length - 1;
  const nextShift = hasNextShift ? state.shifts[state.selectedIndex + 1] : null;
  const canGoForward =
    hasNextShift && nextShift
      ? dayjs(nextShift.shift_start_time).isBefore(dayjs())
      : false;

  const goToPreviousShift = useCallback(async (): Promise<void> => {
    if (state.selectedIndex > 0) {
      dispatch(shiftsActions.selectPrevious());
      return;
    }

    const earliestShift = state.shifts[0];
    if (!earliestShift) return;

    const result = await fetchShifts({
      tenantName,
      referenceTimestamp: earliestShift.shift_start_time,
      before: SHIFTS_PER_FETCH,
      after: 0,
    });

    if (result.data?.shifts && result.data.shifts.length > 0) {
      dispatch(shiftsActions.addShifts(result.data.shifts, false));
      dispatch(shiftsActions.selectPrevious());
    }
  }, [state.selectedIndex, state.shifts, fetchShifts, tenantName]);

  const goToNextShift = useCallback(async (): Promise<void> => {
    if (state.selectedIndex < state.shifts.length - 1) {
      dispatch(shiftsActions.selectNext());
      return;
    }

    const latestShift = state.shifts[state.shifts.length - 1];
    if (!latestShift) return;

    const result = await fetchShifts({
      tenantName,
      referenceTimestamp: latestShift.shift_end_time,
      before: 0,
      after: SHIFTS_PER_FETCH,
    });

    if (result.data?.shifts && result.data.shifts.length > 0) {
      dispatch(shiftsActions.addShifts(result.data.shifts, false));
      dispatch(shiftsActions.selectNext());
    }
  }, [state.selectedIndex, state.shifts, fetchShifts, tenantName]);

  const selectShift = useCallback((shift: CraneShift): void => {
    dispatch(shiftsActions.setTargetTime(shift.shift_start_time));
  }, []);

  const goToTimestamp = useCallback(
    async (targetTime: Dayjs): Promise<void> => {
      const hasShiftsForDate = state.shifts.some(
        (s) =>
          dayjs(s.shift_start_time) <= targetTime &&
          dayjs(s.shift_end_time) > targetTime
      );
      if (!hasShiftsForDate) {
        setOverrideLoading(true);
        const result = await fetchShifts({
          tenantName,
          referenceTimestamp: targetTime.toISOString(),
          before: SHIFTS_PER_FETCH,
          after: SHIFTS_PER_FETCH,
        });
        setOverrideLoading(false);
        dispatch(shiftsActions.addShifts(result.data?.shifts ?? [], false));
      }
      dispatch(shiftsActions.setTargetTime(targetTime.toISOString()));
    },
    [fetchShifts, tenantName, state.shifts]
  );

  const goToDate = useCallback(
    (date: Dayjs, timezone: string): Promise<void> => {
      const localDate = date.tz(timezone);
      const preferredTime = selectedShift?.shift_start_time
        ? dayjs(selectedShift.shift_start_time).tz(timezone)
        : localDate;
      return goToTimestamp(
        parseLocalDateTime(
          `${localDate.format("YYYY-MM-DD")}T${preferredTime.format("HH:mm:ss.SSS")}`,
          timezone
        )
      );
    },
    [goToTimestamp, selectedShift]
  );

  return {
    shifts: state.shifts,
    selectedShift,
    selectedIndex: state.selectedIndex,
    canGoBack,
    canGoForward,
    goToPreviousShift,
    goToNextShift,
    selectShift,
    goToTimestamp,
    goToDate,
    isFetching,
    isLoading: overrideLoading || (isLoading && !state.shifts.length),
    isError,
  };
};
